import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { useCompany } from '../CompanyContext';
import { sans, green, greenDark, greenLight } from '../tokens';
import { getAccessTokenSync } from '../hooks/useAccessToken';

const SB_URL = process.env.REACT_APP_SUPABASE_URL;
const SB_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function sbPost(table, body) {
  const token = getAccessTokenSync();
  const res = await fetch(`${SB_URL}/rest/v1/${table}`, {
    method: 'POST',
    headers: {
      'apikey': SB_KEY,
      'Authorization': `Bearer ${token || SB_KEY}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=minimal',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
}

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const ERP_OPTIONS = [
  'Sage Intacct',
  'QuickBooks Online',
  'QuickBooks Desktop',
  'Xero',
  'NetSuite',
  'SAP',
  'Microsoft Dynamics',
  'FreshBooks',
  'Wave',
  'Other',
];

const INDUSTRY_OPTIONS = [
  'Medical Devices / Medtech',
  'Biotech / Pharma',
  'SaaS / Technology',
  'Manufacturing',
  'Professional Services',
  'Retail / E-commerce',
  'Construction',
  'Real Estate',
  'Non-profit',
  'Other',
];

const CURRENCY_OPTIONS = ['CAD', 'USD', 'EUR', 'GBP', 'Other'];

export default function CompanySetup({ onComplete, onBack }) {
  const { user } = useAuth();
  const { setActiveCompany, refetch } = useCompany();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState({
    companyName: '',
    fiscalYearEndMonth: 12,
    fiscalYearEndDay: 31,
    industry: '',
    currency: 'CAD',
    erp: '',
    multiCurrency: false,
    entityCount: '1',
  });

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  const canNext = () => {
    if (step === 0) return config.companyName.trim().length > 0;
    if (step === 1) return true; // fiscal year always valid
    if (step === 2) return config.industry.length > 0 && config.erp.length > 0;
    return true;
  };

  const handleFinish = async () => {
    setSaving(true);
    setError(null);
    try {
      // Generate company ID client-side (avoids needing RETURNING which hits SELECT RLS)
      const companyId = crypto.randomUUID();

      // Create company via direct REST (supabase-js hangs due to navigator.locks)
      await sbPost('companies', {
        id: companyId,
        name: config.companyName.trim(),
        fiscal_year_end_month: config.fiscalYearEndMonth,
        fiscal_year_end_day: config.fiscalYearEndDay,
        industry: config.industry,
        erp: config.erp,
        currency: config.currency,
        multi_currency: config.multiCurrency,
      });

      // Create admin profile for the current user
      await sbPost('profiles', {
        user_id: user.id,
        company_id: companyId,
        email: user.email,
        full_name: user.user_metadata?.full_name || user.email.split('@')[0],
        initials: (user.user_metadata?.full_name || user.email.split('@')[0])
          .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
        role: 'admin',
      });

      // Refresh company list and select the new company
      await refetch();
      setActiveCompany(companyId);
      onComplete();
    } catch (err) {
      setError(err.message || 'Failed to create company');
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', fontSize: 14, fontFamily: sans,
    border: '1.5px solid #E0E0E0', borderRadius: 8, outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.2s',
    background: '#FAFAFA',
  };

  const selectStyle = { ...inputStyle, appearance: 'none', cursor: 'pointer', background: '#FAFAFA url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\'%3E%3Cpath d=\'M0 0l5 6 5-6z\' fill=\'%239E9E9E\'/%3E%3C/svg%3E") no-repeat right 12px center' };

  const labelStyle = { fontSize: 11, fontWeight: 600, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block', fontFamily: sans };

  const chipStyle = (selected) => ({
    padding: '8px 16px', borderRadius: 20, fontSize: 13, fontFamily: sans,
    border: `1.5px solid ${selected ? green : '#E0E0E0'}`,
    background: selected ? greenLight : '#fff',
    color: selected ? greenDark : '#424242',
    fontWeight: selected ? 600 : 400,
    cursor: 'pointer', transition: 'all 0.15s',
  });

  const steps = [
    // Step 0 — Company Name
    <div key="name">
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: '#212121' }}>What's your company name?</div>
      <div style={{ fontSize: 13, color: '#9E9E9E', marginBottom: 24 }}>This will appear throughout FinKloz.</div>
      <label style={labelStyle}>Company Name</label>
      <input
        type="text"
        value={config.companyName}
        onChange={(e) => update('companyName', e.target.value)}
        placeholder="e.g. Demo Medical Devices Inc."
        autoFocus
        style={inputStyle}
      />
    </div>,

    // Step 1 — Fiscal Year-End
    <div key="fiscal">
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: '#212121' }}>When does your fiscal year end?</div>
      <div style={{ fontSize: 13, color: '#9E9E9E', marginBottom: 24 }}>
        The month matching your year-end will show an additional <strong>Year-End</strong> tab with year-end specific procedures.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={labelStyle}>Month</label>
          <select
            value={config.fiscalYearEndMonth}
            onChange={(e) => update('fiscalYearEndMonth', Number(e.target.value))}
            style={selectStyle}
          >
            {MONTH_OPTIONS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Day</label>
          <select
            value={config.fiscalYearEndDay}
            onChange={(e) => update('fiscalYearEndDay', Number(e.target.value))}
            style={selectStyle}
          >
            {[28, 29, 30, 31].map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: '#9E9E9E', fontFamily: sans }}>
        Most common: Dec 31, Mar 31, Jun 30, Sep 30
      </div>
    </div>,

    // Step 2 — Industry, ERP, Currency
    <div key="details">
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: '#212121' }}>A few more details</div>
      <div style={{ fontSize: 13, color: '#9E9E9E', marginBottom: 24 }}>This helps us tailor your close process.</div>

      <label style={labelStyle}>Industry</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {INDUSTRY_OPTIONS.map(opt => (
          <button key={opt} onClick={() => update('industry', opt)} style={chipStyle(config.industry === opt)}>
            {opt}
          </button>
        ))}
      </div>

      <label style={labelStyle}>ERP / Accounting System</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {ERP_OPTIONS.map(opt => (
          <button key={opt} onClick={() => update('erp', opt)} style={chipStyle(config.erp === opt)}>
            {opt}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={labelStyle}>Reporting Currency</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {CURRENCY_OPTIONS.map(c => (
              <button key={c} onClick={() => update('currency', c)} style={chipStyle(config.currency === c)}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Multi-Currency?</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => update('multiCurrency', true)} style={chipStyle(config.multiCurrency)}>Yes</button>
            <button onClick={() => update('multiCurrency', false)} style={chipStyle(!config.multiCurrency)}>No</button>
          </div>
        </div>
      </div>
    </div>,

    // Step 3 — Review
    <div key="review">
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: '#212121' }}>You're all set!</div>
      <div style={{ fontSize: 13, color: '#9E9E9E', marginBottom: 24 }}>Here's your configuration. You can change these later in Settings.</div>

      <div style={{
        background: '#FAFAFA', borderRadius: 10, padding: '20px 24px',
        border: '1px solid #EEEEEE',
      }}>
        {[
          ['Company', config.companyName],
          ['Fiscal Year-End', `${MONTH_OPTIONS.find(m => m.value === config.fiscalYearEndMonth)?.label} ${config.fiscalYearEndDay}`],
          ['Industry', config.industry],
          ['ERP', config.erp],
          ['Currency', config.currency + (config.multiCurrency ? ' (multi-currency)' : '')],
        ].map(([label, value]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F5F5F5' }}>
            <span style={{ fontSize: 12, color: '#757575', fontFamily: sans }}>{label}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#212121', fontFamily: sans }}>{value}</span>
          </div>
        ))}
      </div>
    </div>,
  ];

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#FAFAFA', fontFamily: sans,
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: '44px 40px 36px', width: 520,
        boxShadow: '0 4px 32px rgba(0,0,0,0.08)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/finkloz-light.png" alt="FinKloz" style={{ height: 53 }} />
          <div style={{ fontSize: 11, color: '#BDBDBD', marginTop: 2 }}>Setup your workspace</div>
          {onBack && (
            <button onClick={onBack} style={{
              marginTop: 8, fontSize: 11, color: '#9E9E9E', background: 'none',
              border: 'none', cursor: 'pointer', fontFamily: sans, textDecoration: 'underline',
            }}>
              Back to workspaces
            </button>
          )}
        </div>

        {/* Progress dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 28 }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{
              width: i === step ? 24 : 8, height: 8, borderRadius: 4,
              background: i <= step ? green : '#E0E0E0',
              transition: 'all 0.3s',
            }} />
          ))}
        </div>

        {/* Step content */}
        <div style={{ minHeight: 220 }}>
          {steps[step]}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} style={{
              padding: '10px 20px', fontSize: 13, fontFamily: sans, fontWeight: 500,
              background: 'transparent', border: '1.5px solid #E0E0E0', borderRadius: 8,
              color: '#757575', cursor: 'pointer',
            }}>
              Back
            </button>
          ) : <div />}

          {step < 3 ? (
            <button
              onClick={() => canNext() && setStep(step + 1)}
              disabled={!canNext()}
              style={{
                padding: '10px 24px', fontSize: 13, fontFamily: sans, fontWeight: 600,
                background: canNext() ? green : '#E0E0E0',
                color: canNext() ? '#fff' : '#9E9E9E',
                border: 'none', borderRadius: 8, cursor: canNext() ? 'pointer' : 'default',
                transition: 'all 0.2s',
              }}
            >
              Continue
            </button>
          ) : (
            <button onClick={handleFinish} disabled={saving} style={{
              padding: '10px 28px', fontSize: 13, fontFamily: sans, fontWeight: 600,
              background: saving ? '#9E9E9E' : green, color: '#fff', border: 'none', borderRadius: 8,
              cursor: saving ? 'default' : 'pointer',
            }}>
              {saving ? 'Creating...' : 'Launch FinKloz'}
            </button>
          )}
        </div>

        {error && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#C0392B', textAlign: 'center' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
